import { Injectable } from '@angular/core';
import { Credentials } from '../../shared/objects/auth/credentials';
import { UserInfo } from '../../shared/objects/auth/userinfo';
import { AuthConstants } from '../../shared/const/authConstants';
import { environment } from '../../../environments/environment';
import { HttpHeaders, HttpClient } from '@angular/common/http';
import { UserRole } from '../../shared/objects/auth/userRole';
import { LoginStatusEnum } from '../../shared/objects/enum/loginStatus';
import { OAuthService, OAuthErrorEvent } from 'angular-oauth2-oidc';
import { HttpResponseStatusCode } from '../../shared/objects/enum/httpResponseCode';
import { Module } from '../../shared/objects/auth/module';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { UserInactivityService } from './user-inactivity.service';

import { AuthConfig } from 'angular-oauth2-oidc';
import { AdminEmails } from '../../shared/components/filter-menu/models/admin-emails';
import { LoginAttempt } from '../../shared/objects/general/login-attempt.model';
import { LoginStatusModel } from '../../shared/objects/general/login-status.model';
import { MfaChallenge, MfaParams } from '../../shared/objects/auth/mfa';
import jwtDecode from 'jwt-decode';

const authCodeFlowConfig: AuthConfig = {
  issuer: environment.keycloakRealmUrl,
  redirectUri: '',
  clientId: environment.keycloakClient,
  responseType: 'code',
  scope: 'openid',
  showDebugInformation: true,
  dummyClientSecret: environment.keycloakClientSecret,
  requireHttps: environment.useHttps,
  oidc: false,
  timeoutFactor: 0.98,
};

@Injectable()
export class AuthService {
  private sourceTimeoutStatus = new BehaviorSubject<boolean>(false);
  private oauthEventsSubscription: Subscription;
  private userActivitySubscription: Subscription;
  userIsLogged = new BehaviorSubject<boolean>(false);

  timeoutStatus = this.sourceTimeoutStatus.asObservable();
  public config = authCodeFlowConfig;

  constructor(
    private httpClient: HttpClient,
    private oauthService: OAuthService,
    private inactivityService: UserInactivityService,
  ) {}
  isImpersonated(): boolean {
    const token = this.getToken();
    if (!token) {
      return false;
    }
    const tokenDecoded = jwtDecode(token);
    return tokenDecoded['impersonator_username']?.length > 0;
  }

  init() {
    this.oauthService.configure(authCodeFlowConfig);
    this.oauthService.setStorage(localStorage);
    this.oauthService.loadDiscoveryDocument();
  }

  public updateUserLoginState(status: boolean): void {
    this.userIsLogged.next(status);
  }
  login(
    credentials: Credentials,
    mfa: MfaParams = {},
  ): Promise<LoginStatusModel> {
    return this.loginWithOAuth(credentials, mfa)
      .then((response: LoginStatusModel) => {
        return this.resetLoginAttempt(credentials.username).then(r => {
          if (r === true) {
            const loginAttempt: LoginStatusModel = {
              attempts: null,
              status: LoginStatusEnum.PASSWORD_RESET,
            };
            return loginAttempt;
          } else {
            this.userIsLogged.next(true);
            return response;
          }
        });
      })
      .catch<LoginStatusModel>(responseError => {
        const loginStatus: LoginStatusModel = {
          attempts: null,
          status: null,
        };
        const body = responseError?.error ?? {};

        // Second factor (Keycloak "direct-grant-carrierportal-mfa"): the password was correct, so
        // these must not count as failed password attempts.
        switch (body.error) {
          case 'mfa_required':
            loginStatus.status = LoginStatusEnum.MFA_REQUIRED;
            loginStatus.mfaChallenge = body as MfaChallenge;
            return loginStatus;
          case 'mfa_setup_required':
            loginStatus.status = LoginStatusEnum.MFA_SETUP_REQUIRED;
            loginStatus.mfaChallenge = body as MfaChallenge;
            return loginStatus;
          case 'invalid_mfa_code':
            loginStatus.status = LoginStatusEnum.INVALID_MFA_CODE;
            return loginStatus;
          case 'invalid_recovery_code':
            loginStatus.status = LoginStatusEnum.INVALID_RECOVERY_CODE;
            return loginStatus;
          default:
            break;
        }

        // Keycloak 26 answers 400 invalid_grant for wrong credentials (Keycloak 17 used 401);
        // other invalid_grant reasons (account disabled, not fully set up) keep meaning "blocked".
        const wrongCredentials =
          body.error_description === 'Invalid user credentials' ||
          responseError.status === HttpResponseStatusCode.Unauthorized;
        if (wrongCredentials) {
          return this.processLoginAttempt(credentials.username)
            .then(r => {
              loginStatus.attempts = r.attempts;
              loginStatus.status =
                r.shouldReset == true
                  ? LoginStatusEnum.PASSWORD_RESET
                  : LoginStatusEnum.INVALID_CREDENTIALS;

              return loginStatus;
            })
            .catch(() => {
              loginStatus.status = LoginStatusEnum.INVALID_CREDENTIALS;
              return loginStatus;
            });
        }
        loginStatus.status =
          responseError.status === HttpResponseStatusCode.BadRequest
            ? LoginStatusEnum.BLOCKED
            : LoginStatusEnum.ERROR;
        return loginStatus;
      });
  }

  private processLoginAttempt(username: string): Promise<LoginAttempt> {
    const headers = this.getBearerRequestHeadersAnonymous();
    return this.httpClient
      .get<LoginAttempt>(
        `${environment.anonymousServiceUrl}/loginAttempt/${username}`,
        { headers },
      )
      .toPromise();
  }

  loginWithOAuth(
    credentials: Credentials,
    mfa: MfaParams = {},
  ): Promise<LoginStatusModel> {
    const loginAttempt: LoginStatusModel = {
      attempts: null,
      status: LoginStatusEnum.SUCCESS,
    };
    const params: { [key: string]: string } = {
      username: credentials.username,
      password: credentials.password,
      device_token: this.getDeviceToken(credentials.username),
      device_name: this.getDeviceName(),
    };
    for (const [key, value] of Object.entries(mfa)) {
      if (value !== undefined && value !== null && value !== '') {
        params[key] = String(value);
      }
    }
    return this.oauthService
      .fetchTokenUsingGrant('password', params)
      .then(() => {
        return this.oauthService
          .loadUserProfile()
          .then(profile => {
            const userInfo = profile['info'] as UserInfo;
            const role = this.getUserRole();

            if (
              role === UserRole.BusinessAdmin &&
              !userInfo.business_unit_urls?.includes(
                this.getCurrentBusinessUnitUrl(),
              )
            ) {
              this.oauthService.logOut();
              loginAttempt.status = LoginStatusEnum.INVALID_CREDENTIALS;
              return loginAttempt;
            }

            this.setupAccessTokenTimeoutContext();

            loginAttempt.status = LoginStatusEnum.SUCCESS;
            return loginAttempt;
          })
          .catch(() => {
            loginAttempt.status = LoginStatusEnum.ERROR;

            return loginAttempt;
          });
      });
  }

  /**
   * Random per-browser, per-user token; Keycloak only trusts it (skips the authenticator code) after
   * a login with "remember this device", and stores just its hash.
   */
  private getDeviceToken(username: string): string {
    const key = `cp-mfa-device:${(username ?? '').trim().toLowerCase()}`;
    let token = localStorage.getItem(key);
    if (!token || !/^[A-Za-z0-9_-]{32,128}$/.test(token)) {
      const bytes = new Uint8Array(32);
      crypto.getRandomValues(bytes);
      token = btoa(String.fromCharCode(...Array.from(bytes)))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');
      localStorage.setItem(key, token);
    }
    return token;
  }

  private getDeviceName(): string {
    const ua = navigator.userAgent;
    const browser =
      [
        ['Edg/', 'Edge'],
        ['OPR/', 'Opera'],
        ['Firefox/', 'Firefox'],
        ['Chrome/', 'Chrome'],
        ['Safari/', 'Safari'],
      ].find(([token]) => ua.includes(token))?.[1] ?? 'Browser';
    const os =
      [
        ['Windows', 'Windows'],
        ['Android', 'Android'],
        ['iPhone', 'iOS'],
        ['iPad', 'iOS'],
        ['Mac OS', 'macOS'],
        ['Linux', 'Linux'],
      ].find(([token]) => ua.includes(token))?.[1] ?? '';
    return os ? `${browser} on ${os}` : browser;
  }

  getCurrentBusinessUnitUrl(): string {
    for (const el of environment.businessunits) {
      if (window.location.host === el.url) {
        return el.url;
      }
    }
    return undefined;
  }

  logout() {
    sessionStorage.clear();
    this.clearSubscriptions();
    this.inactivityService.clearEventListeners();
    this.inactivityService.clearTimeouts();
    this.userIsLogged.next(false);
    // no redirect url after logout
    this.oauthService.logOut(true);
  }

  getBearerRequestHeaders(): HttpHeaders {
    return new HttpHeaders().set('Authorization', 'bearer ' + this.getToken());
  }

  getBearerRequestHeadersAnonymous(): HttpHeaders {
    return new HttpHeaders().set('Authorization', 'bearer');
  }

  getToken(): string {
    return this.oauthService.getAccessToken();
  }

  getUserInfo(): UserInfo {
    return this.oauthService.getIdentityClaims() as UserInfo;
  }

  getStoredUserInfo(): UserInfo {
    return this.oauthService.getIdentityClaims() as UserInfo;
  }

  getUserRole(): UserRole {
    const token = this.getToken();
    if (!token) {
      return UserRole.Unknown;
    }
    const tokenDecoded = jwtDecode(token);
    const roles = tokenDecoded['resource_access']['webapp']['roles'];
    return this.resolveRole(roles);
  }

  private resolveRole(roles: string[]): UserRole {
    while (roles && roles.length !== 0) {
      const role = roles.pop();
      switch (role) {
        case AuthConstants.CARRIER_ADMIN_KEYCLOAK_KEY:
          return UserRole.CarrierAdmin;
        case AuthConstants.CARRIER_USER_KEYCLOAK_KEY:
          return UserRole.CarrierUser;
        case AuthConstants.PORTAL_ADMIN_KEYCLOAK_KEY:
          return UserRole.PortalAdmin;
        case AuthConstants.BUSINESS_ADMIN_KEYCLOAK_KEY:
          return UserRole.BusinessAdmin;
        default:
          break;
      }
    }
    return UserRole.Unknown;
  }

  getUserFeatureClaims(): Module[] {
    const userInfo: UserInfo =
      this.oauthService.getIdentityClaims() as UserInfo;
    let featureClaims: Module[] = [];
    if (userInfo) {
      const featureModules: string[] = userInfo.enabled_modules;

      if (featureModules) {
        featureClaims = featureModules.map<Module>(mod => Module[mod]);
      }
    }
    return featureClaims;
  }

  getUserAdministrationClaims(): Module[] {
    if (!this.oauthService.hasValidAccessToken()) {
      return null;
    }
    const userInfo: UserInfo =
      this.oauthService.getIdentityClaims() as UserInfo;
    let administrationClaims: Module[] = [];
    if (userInfo) {
      const administrationModules: string[] = userInfo.administration_modules;

      if (administrationModules) {
        administrationClaims = administrationModules.map<Module>(
          mod => Module[mod],
        );
      }
    }
    return administrationClaims;
  }

  userHasValidAccessToken(): boolean {
    return this.oauthService.hasValidAccessToken();
  }

  private refreshAccessToken(): void {
    this.oauthService.refreshToken();
  }

  public setupAccessTokenTimeoutContext(): void {
    this.oauthEventsSubscription = this.oauthService.events.subscribe(event => {
      if (event instanceof OAuthErrorEvent) {
      } else {
        switch (event.type) {
          case 'token_expires':
            this.refreshAccessToken();
            break;
          default:
            break;
        }
      }
    });
  }

  public setupUserInactivityTimeoutContext(): void {
    this.inactivityService.setupUserActivityListener();
    this.userActivitySubscription =
      this.inactivityService.userActivity.subscribe(userIsInactive => {
        if (userIsInactive) {
          this.logout();
          this.sourceTimeoutStatus.next(userIsInactive);
        } else {
          this.refreshAccessToken();
        }
      });
  }

  private clearSubscriptions(): void {
    if (!!this.oauthEventsSubscription) {
      this.oauthEventsSubscription.unsubscribe();
    }
    if (!!this.userActivitySubscription) {
      this.userActivitySubscription.unsubscribe();
    }
  }
  public getUserAdminsEmails(username: string): Observable<AdminEmails> {
    return this.httpClient.get<AdminEmails>(
      `/api/usersync/getUserAdminEmails/${username}`,
    );
  }

  private resetLoginAttempt(username: string): Promise<boolean> {
    const headers = this.getBearerRequestHeadersAnonymous();
    return this.httpClient
      .get<boolean>(
        `${environment.anonymousServiceUrl}/resetAttempts/${username}`,
        { headers },
      )
      .toPromise();
  }

  isCurrentUser(username: string | undefined): boolean {
    const userInfo = this.getUserInfo();
    return (
      userInfo?.preferred_username.toLowerCase() === username?.toLowerCase()
    );
  }
}
